import { useState } from "react";
import { toast } from "sonner";
import { Zap, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Formulaire d'estimation express — 6 champs seulement, réponse promise sous 24 h.
 * Envoie /api/contact avec request_type=buyout et un sujet dédié.
 */
export default function ExpressEstimateForm() {
  const [form, setForm] = useState({
    brand_model: "", year: "", mileage: "",
    name: "", phone: "", email: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const message = `— Estimation express 24 h —\nVéhicule : ${form.brand_model}\nAnnée : ${form.year}\nKilométrage : ${form.mileage} km`;
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: "Estimation express 24 h",
        message,
        request_type: "buyout",
      });
      toast.success("Demande reçue — nous vous répondons sous 24 h.");
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Une erreur est survenue");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="express-estimate-success" className="bg-gradient-to-br from-[#1a1a1a] via-[#141414] to-[#0D0D0D] border-2 border-[#C8A75A]/60 rounded-lg p-10 text-center">
        <Zap className="w-10 h-10 text-[#C8A75A] mx-auto mb-4" strokeWidth={1.2} />
        <p className="text-[11px] uppercase tracking-[0.35em] text-[#C8A75A] mb-3">Demande reçue</p>
        <h3 className="font-display text-3xl text-white mb-3 font-light">Merci — nous vous rappelons sous 24 h.</h3>
        <p className="text-white/60 text-sm max-w-md mx-auto">Un membre de l'équipe KV International va analyser votre véhicule sur les cotes Argus / Autoscout24 / Mobile.de et vous adresser une offre ferme écrite.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="express-estimate-form"
      className="relative bg-gradient-to-br from-[#1a1a1a] via-[#141414] to-[#0D0D0D] border-2 border-[#C8A75A]/50 rounded-lg p-8 md:p-10 shadow-[0_20px_60px_-20px_rgba(200,167,90,0.4)]"
    >
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
        <div className="flex items-center gap-4">
          <span className="flex-shrink-0 w-14 h-14 rounded-full bg-[#C8A75A]/15 border border-[#C8A75A]/50 flex items-center justify-center text-[#C8A75A]">
            <Zap className="w-6 h-6" strokeWidth={1.3} />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#C8A75A] mb-1">Estimation express</p>
            <h2 className="font-display text-2xl md:text-3xl text-white font-light leading-tight">Vendez votre véhicule en 24 h</h2>
          </div>
        </div>
        <p className="text-white/60 text-sm max-w-sm">
          Offre ferme écrite sous <strong className="text-white">24 heures</strong> — paiement par virement le jour de la remise des clés.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Véhicule (marque · modèle)" required>
          <Input required data-testid="ee-brand" placeholder="Porsche 991.2 Carrera S" value={form.brand_model} onChange={(e) => set("brand_model", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white h-11" />
        </Field>
        <Field label="Année" required>
          <Input required data-testid="ee-year" type="number" min="1980" max="2027" placeholder="2018" value={form.year} onChange={(e) => set("year", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white h-11" />
        </Field>
        <Field label="Kilométrage" required>
          <Input required data-testid="ee-km" type="number" min="0" placeholder="48 000" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white h-11" />
        </Field>
        <Field label="Votre nom" required>
          <Input required data-testid="ee-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white h-11" />
        </Field>
        <Field label="Téléphone" required>
          <Input required data-testid="ee-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white h-11" />
        </Field>
        <Field label="Email" required>
          <Input required data-testid="ee-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#0D0D0D] border-white/10 text-white h-11" />
        </Field>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">
          Sans engagement · Confidentiel · Frais administratifs pris en charge
        </p>
        <Button type="submit" disabled={sending} data-testid="ee-submit" className="bg-[#C8A75A] text-black hover:bg-white uppercase tracking-widest text-xs px-8 py-6 rounded-md w-full sm:w-auto">
          {sending ? "…" : (<>Recevoir mon offre <ArrowRight className="w-4 h-4 ml-2" /></>)}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-2">
        {label}{required && <span className="text-[#C8A75A]"> *</span>}
      </label>
      {children}
    </div>
  );
}
